import React, { useState } from 'react';
import { Badge, Dropdown, Spinner } from 'react-bootstrap';
import { ChevronDown } from 'lucide-react';

export interface PriorityOption<T extends string> {
    value: T;
    label: string;
    /** Bootstrap background variant for the badge (e.g. 'danger', 'warning'). */
    variant: string;
}

interface EditablePriorityBadgeProps<T extends string> {
    value: T;
    options: PriorityOption<T>[];
    /** When false, renders a plain non-interactive badge. */
    canEdit: boolean;
    /** Persist the new priority. Should handle its own errors (e.g. alert). */
    onChange: (next: T) => Promise<void>;
    className?: string;
    /** Tooltip shown on the editable badge. */
    editTitle?: string;
}

// Custom dropdown toggle so the trigger looks exactly like the static badge
// (no default caret/button chrome), while still wiring up the dropdown.
const BadgeToggle = React.forwardRef<
    HTMLSpanElement,
    React.PropsWithChildren<{ onClick?: (e: React.MouseEvent) => void }>
>(({ children, onClick }, ref) => (
    <span
        ref={ref}
        role="button"
        style={{ cursor: 'pointer' }}
        onClick={(e) => { e.preventDefault(); onClick?.(e); }}
    >
        {children}
    </span>
));
BadgeToggle.displayName = 'BadgeToggle';

/**
 * A priority badge that, for users who can edit, opens a small dropdown of the
 * available priorities when clicked and persists the choice via `onChange`.
 * Looks identical to a static `<Badge>` in read-only mode.
 */
export function EditablePriorityBadge<T extends string>({
    value,
    options,
    canEdit,
    onChange,
    className,
    editTitle = 'Ändra prioritet',
}: EditablePriorityBadgeProps<T>) {
    const [saving, setSaving] = useState(false);

    const current = options.find(o => o.value === value);
    const variant = current?.variant ?? 'secondary';
    const label = current?.label ?? value;

    if (!canEdit) {
        return <Badge bg={variant} className={className}>{label}</Badge>;
    }

    const handleSelect = async (next: T) => {
        if (next === value || saving) return;
        setSaving(true);
        try {
            await onChange(next);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dropdown as="span" style={{ lineHeight: 0 }}>
            <Dropdown.Toggle as={BadgeToggle}>
                <Badge bg={variant} className={`${className ?? ''} editable-priority-badge`.trim()} title={editTitle}>
                    {saving ? <Spinner animation="border" size="sm" /> : (
                        <>
                            {label}
                            {/* Marks the pill as editable */}
                            <ChevronDown size={10} strokeWidth={3} aria-hidden="true" />
                        </>
                    )}
                </Badge>
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {options.map(o => (
                    <Dropdown.Item
                        key={o.value}
                        active={o.value === value}
                        onClick={() => handleSelect(o.value)}
                    >
                        <Badge bg={o.variant} className="me-2 priority-badge">{o.label}</Badge>
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default EditablePriorityBadge;
